/**
 * A section of a settings tab, in Salesforge's settings language.
 *
 * Measured off the reference's tabs: the title at 18/28 600 in the full ink,
 * one grey line under it at 14/20 400, and the section's buttons at the right,
 * centred against both lines rather than against the title alone (Workspace
 * members, API, Workspace webhooks). Then the section's content 20px below.
 *
 * `PageHeader` in its section variant draws the same title with the buttons
 * beside the title only and the line under the row, which is close and visibly
 * not the same when there are two buttons. Screens rebuilt for the settings
 * page draw this. A screen that still draws `PageHeader` keeps working inside
 * the same page, and that is the device engine's, which is not ours to change.
 */
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn'

export interface SettingsSectionProps {
  title: ReactNode
  /** One line under the title. A section has a heading, not an introduction. */
  description?: ReactNode
  /** Buttons at the right. At most one primary. */
  actions?: ReactNode
  children?: ReactNode
  className?: string
  /** The accessible name, when the title is not a plain string. */
  label?: string
}

export function SettingsSection({ title, description, actions, children, className, label }: SettingsSectionProps) {
  return (
    <section aria-label={label ?? (typeof title === 'string' ? title : undefined)} className={cn('space-y-5', className)}>
      <SettingsSectionHead title={title} description={description} actions={actions} />
      {children}
    </section>
  )
}

/** The head alone, for a section whose body is laid out by the caller. */
export function SettingsSectionHead({
  title,
  description,
  actions,
  className,
}: Pick<SettingsSectionProps, 'title' | 'description' | 'actions' | 'className'>) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-3', className)}>
      <div className="min-w-0 flex-1">
        <h2 className="text-xl font-semibold text-ink">{title}</h2>
        {description ? <p className="text-base text-ink-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-3">{actions}</div> : null}
    </div>
  )
}

/**
 * The small group heading between sections, as Salesforge prints `Product
 * Emails` above its notification cards: 14/20 600 in the secondary ink.
 */
export function SettingsGroupLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <h3 className={cn('text-base font-semibold text-ink-secondary', className)}>{children}</h3>
}
